import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getStandardReportPdfBlob } from '@/lib/api/standard-reports';
import { getTcReportPdfBlob } from '@/lib/api/tc-reports';
import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Download, ExternalLink, FileText, GripHorizontal, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inlinePdfShowsFirstPageOnly, openPdfInNewTab } from './inlinePdf';
const DEFAULT_WIDTH = 880;
const HEIGHT_RATIO = 0.86;
const MIN_WIDTH = 420;
const MIN_HEIGHT = 320;
const STORAGE_KEY = 'report-preview-box';
/**
 * The key from when only teleconsultation reports had a quick look. Still read,
 * never written, so a window someone had sized keeps its geometry across the
 * rename; the next move or resize lands under the new key.
 */
const LEGACY_STORAGE_KEY = 'tc-report-preview-box';
function clampToViewport(box) {
    const w = Math.min(Math.max(MIN_WIDTH, box.w), window.innerWidth);
    const h = Math.min(Math.max(MIN_HEIGHT, box.h), window.innerHeight);
    return {
        w,
        h,
        x: Math.min(Math.max(0, box.x), Math.max(0, window.innerWidth - w)),
        y: Math.min(Math.max(0, box.y), Math.max(0, window.innerHeight - h)),
    };
}
function initialBox() {
    try {
        const saved = sessionStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(LEGACY_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (typeof parsed.x === 'number' && typeof parsed.w === 'number') {
                return clampToViewport(parsed);
            }
        }
    }
    catch {
        /* ignore malformed/unavailable storage */
    }
    const w = Math.min(DEFAULT_WIDTH, window.innerWidth - 32);
    const h = Math.round(window.innerHeight * HEIGHT_RATIO);
    return clampToViewport({ w, h, x: (window.innerWidth - w) / 2, y: (window.innerHeight - h) / 2 });
}
/**
 * Quick-look preview of a signed report, shown as a clean, draggable,
 * resizable floating window. Renders the same PDF the download produces (native
 * nav rail hidden via #navpanes=0). Position/size persist for the session.
 *
 * Grew up on teleconsultation reports; `kind="standard"` points it at a signed
 * in-house report instead, so the worklist can open both report kinds the same
 * way. Defaults to teleconsultation so the existing callers are unchanged.
 */
export function ReportPreviewDialog({ reportId, kind = 'teleconsultation', patientName, open, onClose, onOpenFull, }) {
    const { t } = useTranslation('report');
    const [pdfUrl, setPdfUrl] = useState(null);
    const [error, setError] = useState(false);
    const [box, setBox] = useState(initialBox);
    const offerOpenPdf = inlinePdfShowsFirstPageOnly();
    const dragRef = useRef(null);
    const resizeRef = useRef(null);
    useEffect(() => {
        // Only fetch the PDF once the preview is actually open. The dialog is mounted
        // (closed) for every finalized row in the Studies expanded view, so fetching
        // on mount would eagerly generate/download a PDF the user never asked to see.
        if (!open)
            return;
        let cancelled = false;
        let objectUrl = null;
        const run = async () => {
            setPdfUrl(null);
            setError(false);
            try {
                // Both clients take the report id alone and hand back the rendered PDF
                // as a Blob; only the table (and therefore the endpoint) differs.
                const fetchPdf = kind === 'standard' ? getStandardReportPdfBlob : getTcReportPdfBlob;
                const blob = await fetchPdf(reportId);
                if (cancelled)
                    return;
                objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
            }
            catch {
                if (!cancelled)
                    setError(true);
            }
        };
        void run();
        return () => {
            cancelled = true;
            if (objectUrl)
                URL.revokeObjectURL(objectUrl);
        };
    }, [reportId, kind, open]);
    // Persist the window geometry for the session.
    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(box));
        }
        catch {
            /* ignore */
        }
    }, [box]);
    // Re-fit when the viewport changes while open: rotating a tablet or phone
    // (or shrinking a desktop window) otherwise leaves the window sized for the
    // old viewport, with Close and Download off-screen.
    useEffect(() => {
        if (!open)
            return;
        const handleResize = () => setBox((b) => clampToViewport(b));
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [open]);
    const handleDragDown = (e) => {
        if (e.target.closest('button, a'))
            return;
        e.preventDefault();
        dragRef.current = { startX: e.clientX, startY: e.clientY, originX: box.x, originY: box.y };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const handleDragMove = (e) => {
        const drag = dragRef.current;
        if (!drag)
            return;
        const nextX = drag.originX + (e.clientX - drag.startX);
        const nextY = drag.originY + (e.clientY - drag.startY);
        setBox((prev) => clampToViewport({ ...prev, x: nextX, y: nextY }));
    };
    const handleDragUp = (e) => {
        if (dragRef.current) {
            dragRef.current = null;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
        }
    };
    const handleResizeDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        resizeRef.current = { startX: e.clientX, startY: e.clientY, originW: box.w, originH: box.h };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const handleResizeMove = (e) => {
        const rz = resizeRef.current;
        if (!rz)
            return;
        const nextW = rz.originW + (e.clientX - rz.startX);
        const nextH = rz.originH + (e.clientY - rz.startY);
        setBox((prev) => clampToViewport({ ...prev, w: nextW, h: nextH }));
    };
    const handleResizeUp = (e) => {
        if (resizeRef.current) {
            resizeRef.current = null;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
        }
    };
    // Header icon buttons: 24px for a mouse, 36px where a finger may be used.
    const headerButton = 'hover:bg-accent text-muted-foreground hover:text-foreground rounded-sm p-1 transition-colors pointer-coarse:p-2.5';
    return (_jsx(DialogPrimitive.Root, { open: open, modal: false, onOpenChange: (o) => {
            if (!o)
                onClose();
        }, children: _jsx(DialogPrimitive.Portal, { children: _jsxs(DialogPrimitive.Content, { "aria-describedby": undefined, onOpenAutoFocus: (e) => e.preventDefault(), onInteractOutside: (e) => e.preventDefault(), className: "border-border bg-background fixed z-50 flex flex-col overflow-hidden rounded-lg border shadow-2xl", style: { left: box.x, top: box.y, width: box.w, height: box.h, transform: 'none' }, children: [_jsxs("div", { onPointerDown: handleDragDown, onPointerMove: handleDragMove, onPointerUp: handleDragUp, onPointerCancel: handleDragUp, className: "border-border bg-muted/40 flex shrink-0 cursor-move touch-none items-center gap-2 border-b px-3 py-2 select-none", children: [_jsx(GripHorizontal, { className: "text-muted-foreground/50 h-4 w-4 shrink-0", "aria-hidden": true }), _jsx(DialogPrimitive.Title, { className: "min-w-0 flex-1 truncate text-sm font-semibold", children: patientName
                                    ? t('reportPreview.titleFor', { name: patientName })
                                    : t('reportPreview.title') }), offerOpenPdf && pdfUrl && (
                            // iOS/iPadOS paint only page 1 of the inline PDF below.
                            _jsxs("button", { type: "button", "data-open-pdf": "", onClick: () => openPdfInNewTab(pdfUrl), className: cn(headerButton, 'flex shrink-0 items-center gap-1 text-xs'), "aria-label": t('reportPreview.openPdf'), children: [_jsx(FileText, { className: "h-4 w-4" }), _jsx("span", { className: "hidden sm:inline", children: t('reportPreview.openPdf') })] })), onOpenFull && (_jsx("button", { type: "button", onClick: onOpenFull, className: cn(headerButton, 'shrink-0'), "aria-label": t('reportPreview.openFull'), title: t('reportPreview.openFull'), children: _jsx(ExternalLink, { className: "h-4 w-4" }) })), pdfUrl && (_jsx("a", { href: pdfUrl, download: `report-${patientName ?? reportId}.pdf`, className: cn(headerButton, 'shrink-0'), "aria-label": t('reportPreview.download'), title: t('reportPreview.download'), children: _jsx(Download, { className: "h-4 w-4" }) })), _jsx(DialogPrimitive.Close, { className: cn(headerButton, 'shrink-0'), "aria-label": t('reportPreview.close'), children: _jsx(X, { className: "h-4 w-4" }) })] }), _jsx("div", { className: "min-h-0 flex-1 bg-[#525659]", children: error ? (_jsx("div", { className: "text-muted-foreground flex h-full items-center justify-center text-sm", children: t('reportPreview.error') })) : pdfUrl ? (_jsx("iframe", { src: `${pdfUrl}#navpanes=0&view=FitH`, title: t('reportPreview.title'), className: "h-full w-full border-0" })) : (_jsx("div", { className: "text-muted-foreground flex h-full items-center justify-center text-sm", children: t('reportPreview.loading') })) }), _jsx("div", { "data-testid": "report-preview-resize", onPointerDown: handleResizeDown, onPointerMove: handleResizeMove, onPointerUp: handleResizeUp, onPointerCancel: handleResizeUp, className: "absolute right-0 bottom-0 flex h-4 w-4 cursor-nwse-resize touch-none items-end justify-end any-pointer-coarse:h-9 any-pointer-coarse:w-9", "aria-hidden": true, children: _jsx("svg", { viewBox: "0 0 10 10", className: "text-muted-foreground/50 h-4 w-4 shrink-0", children: _jsx("path", { d: "M9 1 L1 9 M9 5 L5 9", stroke: "currentColor", strokeWidth: "1", fill: "none" }) }) })] }) }) }));
}
